import Skeleton from "@mui/material/Skeleton";
import styles from "./main.module.scss";

// Placeholders shaped like the content they stand in for, so the page keeps its
// layout while data loads instead of collapsing to a spinner.

// the app's sidebar green rather than MUI's grey, held still for anyone whose
// system asks for less motion
const barSx = {
  bgcolor: "rgba(51, 86, 66, 0.11)",
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    "&::after": { display: "none" },
  },
};

// bars of one length read as a grid of blocks; varying them reads as text
const widths = ["70%", "45%", "85%", "55%", "65%", "40%", "75%"];

interface BarProps {
  width?: string;
  height?: string;
}

const Bar = ({ width = "100%", height = "1.6rem" }: BarProps) => (
  <Skeleton
    variant="rounded"
    animation="wave"
    width={width}
    height={height}
    sx={barSx}
  />
);

const range = (length: number) => Array.from({ length }, (_, i) => i);

interface LoadingProps {
  className?: string;
  children: React.ReactNode;
}

// the bars have no text, so this is what a screen reader announces
const Loading = ({ className, children }: LoadingProps) => (
  <div role="status" className={className}>
    <span className={styles.srOnly}>Loading...</span>
    {children}
  </div>
);

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

const TableBars = ({ rows = 8, columns = 6 }: TableSkeletonProps) => (
  <div className={styles.table}>
    <div className={styles.tableHead}>
      {range(columns).map((col) => (
        <div className={styles.cell} key={col}>
          <Bar width="60%" height="1.8rem" />
        </div>
      ))}
    </div>
    {range(rows).map((row) => (
      <div className={styles.tableRow} key={row}>
        {range(columns).map((col) => (
          <div className={styles.cell} key={col}>
            <Bar width={widths[(row + col) % widths.length]} />
          </div>
        ))}
      </div>
    ))}
  </div>
);

/** Stands in for a table while its rows load. */
export const TableSkeleton = (props: TableSkeletonProps) => (
  <Loading>
    <TableBars {...props} />
  </Loading>
);

interface DetailsSkeletonProps {
  rows?: number;
}

/** Stands in for label and value pairs: member details, forms and profile tabs. */
export const DetailsSkeleton = ({ rows = 6 }: DetailsSkeletonProps) => (
  <Loading className={styles.details}>
    {range(rows).map((row) => (
      <div className={styles.field} key={row}>
        <Bar width="40%" height="1.4rem" />
        <Bar width={widths[row % widths.length]} height="2rem" />
      </div>
    ))}
  </Loading>
);

interface ChartSkeletonProps {
  height?: string;
}

/** Stands in for a chart, at the chart's own height. */
export const ChartSkeleton = ({ height = "30rem" }: ChartSkeletonProps) => (
  <Loading>
    <Bar height={height} />
  </Loading>
);

/** Shown while a list page's code loads: its header, filters and table. */
export const PageSkeleton = () => (
  <Loading className={styles.page}>
    <div className={styles.pageHead}>
      <Bar width="18rem" height="3.2rem" />
      <Bar width="14rem" height="4.4rem" />
    </div>
    <div className={styles.filters}>
      {range(4).map((i) => (
        <Bar key={i} height="4.4rem" />
      ))}
    </div>
    <TableBars />
  </Loading>
);

/** Shown while the overview loads: its cards, each titled over a chart. */
export const DashboardSkeleton = () => (
  <Loading className={styles.dashboard}>
    {range(4).map((i) => (
      <div className={styles.card} key={i}>
        <Bar width="40%" height="1.8rem" />
        <Bar height="30rem" />
      </div>
    ))}
  </Loading>
);

/** Shown while a sign-in page loads: a heading, two fields and a button. */
export const AuthSkeleton = () => (
  <Loading className={styles.auth}>
    <Bar width="60%" height="3.2rem" />
    <Bar width="85%" />
    {range(2).map((i) => (
      <div className={styles.field} key={i}>
        <Bar width="30%" height="1.4rem" />
        <Bar height="4.8rem" />
      </div>
    ))}
    <Bar height="4.8rem" />
  </Loading>
);
